import type { AppField, AppMeta } from './meta.js';
import { optionsFor } from './meta.js';

/** How values read on screen. Stored values stay as the API sends them; this is display only. */

export const LOCALE = 'en-US';

const dateFormat = new Intl.DateTimeFormat(LOCALE, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});
const regions = new Intl.DisplayNames([LOCALE], { type: 'region', fallback: 'code' });
const currencies = new Intl.DisplayNames([LOCALE], { type: 'currency', fallback: 'code' });

export function formatMoney(value: unknown, currency: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  if (!currency || !/^[A-Z]{3}$/.test(currency))
    return amount.toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return new Intl.NumberFormat(LOCALE, { style: 'currency', currency }).format(amount);
}

/** A headline amount in whole units: $303,612. The cents are in the lists behind it. */
export function formatWholeMoney(value: unknown, currency: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  if (!currency || !/^[A-Z]{3}$/.test(currency))
    return amount.toLocaleString(LOCALE, { maximumFractionDigits: 0 });
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(amount);
}

/** An amount on a chart's scale: $325.4K. */
export function formatCompactMoney(value: number, currency: string | null | undefined): string {
  const options: Intl.NumberFormatOptions = { notation: 'compact', maximumFractionDigits: 1 };
  if (currency && /^[A-Z]{3}$/.test(currency)) {
    options.style = 'currency';
    options.currency = currency;
  }
  return new Intl.NumberFormat(LOCALE, options).format(value);
}

export function formatDate(value: unknown): string {
  if (typeof value !== 'string' || value === '') return '';
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(date.getTime()) ? value : dateFormat.format(date);
}

const longDateFormat = new Intl.DateTimeFormat(LOCALE, {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/** A calendar date in running text: September 23, 2026. */
export function formatLongDate(value: string): string {
  const date = new Date(`${value.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? value : longDateFormat.format(date);
}

export function formatDateTime(value: unknown, timeZone?: string): string {
  if (typeof value !== 'string' || value === '') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(LOCALE, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...(timeZone && { timeZone }),
  }).format(date);
}

export function formatNumber(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : n.toLocaleString(LOCALE, { maximumFractionDigits: 4 });
}

export function countryName(code: unknown): string {
  return typeof code === 'string' && code ? (regions.of(code) ?? code) : '';
}

export function currencyName(code: unknown): string {
  return typeof code === 'string' && code ? `${code} · ${currencies.of(code) ?? code}` : '';
}

/** Every ISO country the runtime can name, for country pickers. */
export const COUNTRIES: { value: string; label: string }[] = (() => {
  const out: { value: string; label: string }[] = [];
  const skip = new Set(['EU', 'EZ', 'UN', 'QO', 'XA', 'XB', 'ZZ']);
  for (let a = 65; a <= 90; a++) {
    for (let b = 65; b <= 90; b++) {
      const code = String.fromCharCode(a, b);
      const name = regions.of(code);
      if (!skip.has(code) && name && name !== code) out.push({ value: code, label: name });
    }
  }
  return out.sort((x, y) => x.label.localeCompare(y.label));
})();

export const CURRENCIES: string[] = (() => {
  try {
    return Intl.supportedValuesOf('currency');
  } catch {
    return ['AUD', 'CAD', 'CHF', 'EUR', 'GBP', 'JPY', 'NZD', 'SEK', 'USD'];
  }
})();

/** A value as text, for tables and summaries. References need `titles` (id to title). */
export function displayText(
  meta: AppMeta,
  field: AppField,
  record: Record<string, unknown>,
  titles: Record<string, string> = {},
): string {
  const value = record[field.key];
  if (value === null || value === undefined || value === '') return '';
  switch (field.type) {
    case 'money': {
      const currency = record[field.currency_field ?? 'currency'];
      return formatMoney(
        value,
        typeof currency === 'string' ? currency : meta.organization.base_currency,
      );
    }
    case 'date':
      return formatDate(value);
    case 'datetime':
      return formatDateTime(value, meta.organization.timezone);
    case 'integer':
    case 'decimal':
      return formatNumber(value);
    case 'percent':
      return `${formatNumber(value)}%`;
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'select': {
      const option = optionsFor(meta, field, record).find((o) => o.value === value);
      return option?.label ?? String(value);
    }
    case 'multi_select': {
      const options = optionsFor(meta, field, record);
      return (Array.isArray(value) ? value : [value])
        .map((v) => options.find((o) => o.value === v)?.label ?? String(v))
        .join(', ');
    }
    case 'tags':
      return Array.isArray(value) ? value.join(', ') : String(value);
    case 'reference':
      return titles[String(value)] ?? 'Untitled';
    case 'country':
      return countryName(value);
    case 'currency':
      return String(value);
    case 'json':
      return JSON.stringify(value);
    default:
      return String(value);
  }
}

/** A record's title, with a fallback for documents not yet numbered. */
export function recordTitle(
  entity: {
    title_field: string;
    label: string;
    initial_state?: string;
    fields?: readonly Pick<AppField, 'key' | 'options'>[];
  },
  record: Record<string, unknown>,
): string {
  const title = record[entity.title_field];
  if (title !== null && title !== undefined && title !== '') {
    // A coded title, such as a plan, reads as its option's label.
    const field = entity.fields?.find((f) => f.key === entity.title_field);
    return field?.options?.find((o) => o.value === title)?.label ?? String(title);
  }
  return record['state'] === 'draft'
    ? `Draft ${entity.label.toLowerCase()}`
    : `Untitled ${entity.label.toLowerCase()}`;
}
